import type { MessageStatus } from '@fundline/shared/messages/status';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

export const INBOX_PAGE_SIZE = 50;
/** Texts shown in one conversation (newest). */
export const CONVERSATION_LIMIT = 200;

export type InboxView =
  'all' | 'unread' | 'needs_reply' | 'scheduled' | 'problems' | 'opted_out' | 'do_not_contact';

export interface InboxFilters {
  view: InboxView;
  /** Assigned rep (admins only; reps only ever see their own). */
  rep: string | null;
  /** Only merchants who got a text from this campaign (M9; from the campaign's screen). */
  campaign?: string | null;
}

export interface InboxItem {
  merchantId: string;
  phone: string;
  businessName: string | null;
  ownerName: string | null;
  assignedRepId: string;
  lastMessageAt: string;
  preview: string | null;
  lastDirection: 'in' | 'out' | null;
  lastStatus: MessageStatus | null;
  unreadCount: number;
  needsReply: boolean;
  sendProblem: string | null;
  optedOut: boolean;
  doNotContact: boolean;
  /** Held texts waiting to go out (M7: quiet hours, caps, a missing state…). */
  heldCount: number;
  nextHeldAt: string | null;
}

/** One page of conversations, newest first. RLS decides which merchants come back. */
export async function fetchInboxPage(
  workspaceId: string,
  filters: InboxFilters,
  offset: number,
): Promise<InboxItem[]> {
  const select =
    'id, phone, business_name, owner_name, assigned_rep_id, last_message_at, last_message_preview, last_message_direction, last_message_status, unread_count, needs_reply, send_problem, opted_out_at, do_not_contact, held_count, next_held_at' +
    (filters.campaign ? ', campaign_texts:messages!inner(campaign_id)' : '');
  let query = supabase()
    .from('merchants')
    .select(select)
    .eq('workspace_id', workspaceId)
    .not('last_message_at', 'is', null)
    .is('archived_at', null);
  if (filters.view === 'unread') query = query.gt('unread_count', 0);
  if (filters.view === 'needs_reply') query = query.eq('needs_reply', true);
  if (filters.view === 'problems') query = query.not('send_problem', 'is', null);
  if (filters.view === 'opted_out') query = query.not('opted_out_at', 'is', null);
  if (filters.view === 'do_not_contact') query = query.eq('do_not_contact', true);
  if (filters.view === 'scheduled') query = query.gt('held_count', 0);
  if (filters.rep) query = query.eq('assigned_rep_id', filters.rep);
  if (filters.campaign) query = query.eq('campaign_texts.campaign_id', filters.campaign);
  // Scheduled: what goes out next comes first.
  if (filters.view === 'scheduled') query = query.order('next_held_at', { ascending: true });
  const { data, error } = await query
    .order('last_message_at', { ascending: false })
    .order('id')
    .range(offset, offset + INBOX_PAGE_SIZE - 1)
    .overrideTypes<
      Array<{
        id: string;
        phone: string;
        business_name: string | null;
        owner_name: string | null;
        assigned_rep_id: string;
        last_message_at: string | null;
        last_message_preview: string | null;
        last_message_direction: 'in' | 'out' | null;
        last_message_status: MessageStatus | null;
        unread_count: number;
        needs_reply: boolean;
        send_problem: string | null;
        opted_out_at: string | null;
        do_not_contact: boolean;
        held_count: number;
        next_held_at: string | null;
      }>,
      { merge: false }
    >();
  if (error) throw new Error(`Couldn't load the inbox: ${error.message}`);
  return data.map((m) => ({
    merchantId: m.id,
    phone: m.phone,
    businessName: m.business_name,
    ownerName: m.owner_name,
    assignedRepId: m.assigned_rep_id,
    lastMessageAt: m.last_message_at ?? '',
    preview: m.last_message_preview,
    lastDirection: m.last_message_direction,
    lastStatus: m.last_message_status,
    unreadCount: m.unread_count,
    needsReply: m.needs_reply,
    sendProblem: m.send_problem,
    optedOut: m.opted_out_at !== null,
    doNotContact: m.do_not_contact,
    heldCount: m.held_count,
    nextHeldAt: m.next_held_at,
  }));
}

export function useInboxQuery(workspaceId: string, filters: InboxFilters) {
  return useInfiniteQuery<
    InboxItem[],
    Error,
    InfiniteData<InboxItem[]>,
    ReturnType<typeof queryKeys.inboxList>,
    number
  >({
    queryKey: queryKeys.inboxList(workspaceId, filters),
    queryFn: ({ pageParam }) => fetchInboxPage(workspaceId, filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.length === INBOX_PAGE_SIZE ? pages.length * INBOX_PAGE_SIZE : undefined,
  });
}

export interface ConversationMessage {
  id: string;
  direction: 'in' | 'out';
  kind: string;
  body: string;
  status: MessageStatus;
  statusReason: string | null;
  notBefore: string | null;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  senderUserId: string | null;
  segments: number | null;
  isRead: boolean;
  resendOfId: string | null;
  /** Which sending window applies (outbound), and when it was last handed to the provider (M7 caps). */
  msgClass: 'campaign' | 'reply' | null;
  lastAttemptAt: string | null;
  /** M9: the campaign a text came from, and whether it was that campaign's test. */
  campaignId: string | null;
  campaignTest: boolean;
  /** Shown before the database answers (the compose box's own optimistic copy). */
  pending?: boolean;
}

export interface MerchantHeader {
  id: string;
  phone: string;
  businessName: string | null;
  ownerName: string | null;
  email: string | null;
  state: string | null;
  assignedRepId: string;
  archivedAt: string | null;
  optedOutAt: string | null;
  doNotContact: boolean;
  dncReason: string | null;
  lineType: string;
  stickyNumberId: string | null;
  timezone: string | null;
  timezoneSource: string;
}

export interface Conversation {
  merchant: MerchantHeader | null;
  messages: ConversationMessage[];
  /** One of our texts was resent already (at most one resend each). */
  resentIds: Set<string>;
  truncated: boolean;
}

export async function fetchConversation(
  workspaceId: string,
  merchantId: string,
): Promise<Conversation> {
  const [merchant, messages] = await Promise.all([
    supabase()
      .from('merchants')
      .select(
        'id, phone, business_name, owner_name, email, state, assigned_rep_id, archived_at, opted_out_at, do_not_contact, dnc_reason, line_type, sticky_number_id, timezone, timezone_source',
      )
      .eq('workspace_id', workspaceId)
      .eq('id', merchantId)
      .maybeSingle(),
    supabase()
      .from('messages')
      .select(
        'id, direction, kind, body, status, status_reason, not_before, created_at, updated_at, sent_at, delivered_at, failed_at, sender_user_id, segments, is_read, resend_of_id, class, last_attempt_at, campaign_id, campaign_test',
      )
      .eq('workspace_id', workspaceId)
      .eq('merchant_id', merchantId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(CONVERSATION_LIMIT),
  ]);
  if (merchant.error) throw new Error(`Couldn't load the merchant: ${merchant.error.message}`);
  if (messages.error) throw new Error(`Couldn't load the texts: ${messages.error.message}`);
  const m = merchant.data;
  const list: ConversationMessage[] = messages.data
    .map((x) => ({
      id: x.id,
      direction: x.direction,
      kind: x.kind,
      body: x.body,
      status: x.status,
      statusReason: x.status_reason,
      notBefore: x.not_before,
      createdAt: x.created_at,
      updatedAt: x.updated_at,
      sentAt: x.sent_at,
      deliveredAt: x.delivered_at,
      failedAt: x.failed_at,
      senderUserId: x.sender_user_id,
      segments: x.segments,
      isRead: x.is_read,
      resendOfId: x.resend_of_id,
      msgClass: x.class,
      lastAttemptAt: x.last_attempt_at,
      campaignId: x.campaign_id,
      campaignTest: x.campaign_test,
    }))
    .reverse();
  return {
    merchant: m
      ? {
          id: m.id,
          phone: m.phone,
          businessName: m.business_name,
          ownerName: m.owner_name,
          email: m.email,
          state: m.state,
          assignedRepId: m.assigned_rep_id,
          archivedAt: m.archived_at,
          optedOutAt: m.opted_out_at,
          doNotContact: m.do_not_contact,
          dncReason: m.dnc_reason,
          lineType: m.line_type,
          stickyNumberId: m.sticky_number_id,
          timezone: m.timezone,
          timezoneSource: m.timezone_source,
        }
      : null,
    messages: list,
    resentIds: new Set(list.flatMap((x) => (x.resendOfId ? [x.resendOfId] : []))),
    truncated: messages.data.length === CONVERSATION_LIMIT,
  };
}

export function useConversationQuery(workspaceId: string, merchantId: string) {
  return useQuery({
    queryKey: queryKeys.conversation(workspaceId, merchantId),
    queryFn: () => fetchConversation(workspaceId, merchantId),
  });
}

/** The opt-out footer the next text will get (first text to this merchant), or null. */
export function useMessageFooterQuery(workspaceId: string, merchantId: string) {
  return useQuery({
    queryKey: queryKeys.messageFooter(workspaceId, merchantId),
    queryFn: async () => {
      const { data, error } = await supabase().rpc('message_footer', { p_merchant_id: merchantId });
      if (error) throw new Error(`Couldn't check the opt-out footer: ${error.message}`);
      return data;
    },
  });
}

/** Database errors from the send RPCs, as sentences. */
export function sendErrorMessage(err: { code?: string; message: string }): string {
  if (err.code === '42501' && err.message.includes('row-level security')) {
    return 'You cannot text this merchant.';
  }
  return err.message.charAt(0).toUpperCase() + err.message.slice(1);
}

export function useInboxMutations(workspaceId: string, merchantId: string) {
  const queryClient = useQueryClient();
  const key = queryKeys.conversation(workspaceId, merchantId);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) });

  const send = useMutation({
    mutationFn: async (input: { body: string; finalText: string; clientKey: string }) => {
      const { data, error } = await supabase().rpc('send_message', {
        p_merchant_id: merchantId,
        p_body: input.body,
        p_client_key: input.clientKey,
      });
      if (error) throw new Error(sendErrorMessage(error));
      return data;
    },
    // The sender's own text shows at once; the refetch replaces it with the stored row.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Conversation>(key);
      if (previous) {
        const now = new Date().toISOString();
        queryClient.setQueryData<Conversation>(key, {
          ...previous,
          messages: [
            ...previous.messages,
            {
              id: `pending-${input.clientKey}`,
              direction: 'out',
              kind: 'manual',
              body: input.finalText,
              status: 'queued',
              statusReason: null,
              notBefore: null,
              createdAt: now,
              updatedAt: now,
              sentAt: null,
              deliveredAt: null,
              failedAt: null,
              senderUserId: null,
              segments: null,
              isRead: true,
              resendOfId: null,
              msgClass: 'reply',
              lastAttemptAt: null,
              campaignId: null,
              campaignTest: false,
              pending: true,
            },
          ],
        });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: invalidate,
  });

  const resend = useMutation({
    mutationFn: async (messageId: string) => {
      const { data, error } = await supabase().rpc('resend_message', { p_message_id: messageId });
      if (error) throw new Error(sendErrorMessage(error));
      return data;
    },
    onSettled: invalidate,
  });

  const markRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase().rpc('mark_conversation_read', {
        p_merchant_id: merchantId,
      });
      if (error) throw new Error(error.message);
    },
    onSettled: invalidate,
  });

  return { send, resend, markRead };
}
